import { type ComponentProps, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <Label htmlFor={htmlFor}>{children}</Label>
      {required ? (
        <span aria-hidden="true" className="text-sm leading-none text-destructive">
          *
        </span>
      ) : null}
    </div>
  );
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-destructive">
      {message}
    </p>
  );
}

export function TextField({
  id,
  label,
  required,
  error,
  className,
  ...input
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
} & Pick<ComponentProps<'input'>, 'value' | 'onChange' | 'inputMode' | 'type' | 'disabled'>) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <Input
        id={id}
        autoComplete="off"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        {...input}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}
